import { Button } from "@/components/ui/button";
import sanitizeHTML from "@/lib/sanitizeHTML";
import { cn } from "@/lib/utils";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface MailSignatureBlockProps {
    html: string;
    onChange: (html: string) => void;
}

// Rendered outside the editor content (re-joined on send) so a bullet list can
// never land inside the signature (AB#1746).
const MailSignatureBlock = ({ html, onChange }: MailSignatureBlockProps) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    // Stable object: React 19 rewrites innerHTML on a new one, wiping an in-progress edit.
    const innerHtml = useMemo(() => ({ __html: sanitizeHTML(html) }), [html]);

    // Remember the last signature shown so "add" can restore it after a delete.
    const lastHtmlRef = useRef(html);
    useEffect(() => {
        if (html) {
            lastHtmlRef.current = html;
        }
    }, [html]);

    useEffect(() => {
        if (isEditing) {
            contentRef.current?.focus();
        }
    }, [isEditing]);

    const stopEditing = () => {
        setIsEditing(false);
        if (contentRef.current) {
            onChange(contentRef.current.innerHTML);
        }
    };

    if (!html) {
        if (!lastHtmlRef.current) {
            return null;
        }

        return (
            <div className="mt-2 flex justify-end">
                <Button
                    icon={<Plus />}
                    onClick={() => onChange(lastHtmlRef.current)}
                    size="sm"
                    variant="outline"
                >
                    {t("email.addSignatureForThisMail")}
                </Button>
            </div>
        );
    }

    return (
        <div className="group relative mt-2">
            {!isEditing && (
                <div className="absolute right-1 top-1 z-10 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                        icon={<Pencil />}
                        onClick={() => setIsEditing(true)}
                        size="sm"
                        variant="outline"
                    >
                        {t("email.editSignatureForThisMail")}
                    </Button>
                    <Button
                        aria-label={t("email.deleteSignatureForThisMail")}
                        className="rounded-md hover:text-destructive"
                        icon={<Trash2 />}
                        onClick={() => onChange("")}
                        size="smIcon"
                        title={t("email.deleteSignatureForThisMail")}
                        variant="outline"
                    />
                </div>
            )}
            <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={innerHtml}
                ref={contentRef}
                className={cn(
                    "rounded-md p-2 outline-none transition-colors",
                    isEditing && "cursor-text ring-2 ring-primary-default",
                )}
                contentEditable={isEditing}
                onBlur={stopEditing}
                suppressContentEditableWarning
            />
            {isEditing && (
                <p className="mt-1 px-2 text-xs text-text-secondary">
                    {t("email.signatureEditingHint")}
                </p>
            )}
        </div>
    );
};

export default MailSignatureBlock;
